import { NavLink, Outlet } from 'react-router-dom'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import * as React from 'react'
import { Col, Container, Row, ToastContainer } from 'react-bootstrap'
import { MenuItem, Sidebar, Menu, menuClasses, MenuItemStyles, sidebarClasses } from 'react-pro-sidebar'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { SidebarHeader } from './SidebarHeader'
import { SidebarFooter } from './SidebarFooter'
import { LayoutHeader } from './LayoutHeader'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useOutsideClick } from '../../common/useOutsideClick'
import { Toast } from '../../components/Toast/Toast'
import { removeToast, selectToasts } from '../../store/toast/toastSlice'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { HeaderContextProvider } from './HeaderContext'
import styled from '@emotion/styled'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import './LayoutWithLeftSidebar.scss'
import { LiveIcon } from '../../components/LiveMeetings/LiveIcon'
import { Divider } from '../../components/Divider/Divider'
import { useTrackedMenuItem } from './useTrackedMenuItem'
import { isInsightsEnabled } from '../../common/constants'
import { useIframeMessageHandler } from '../../common/useIframeMessageHandler'
import { FeatureAdvertisement } from '../../components/FeatureAdvertisement/FeatureAdvertisement'

interface LayoutProps extends PropsWithChildren {
  containerClassName?: string
}

const menuItemStyles: MenuItemStyles = {
  root: {
    color: 'white',
    borderRadius: '0.25rem',
  },
  icon: {
    color: 'white',
    width: 'auto',
    minWidth: 'auto',
    [`&.${menuClasses.disabled}`]: {
      color: 'white',
    },
    marginRight: 0,
  },
  SubMenuExpandIcon: {
    color: '#b6b7b9',
  },
  subMenuContent: ({ level }) => ({
    backgroundColor: level === 0 ? 'blue' : 'transparent',
  }),
  button: {
    height: '2.125rem',
    padding: '0.5rem',
    gap: '0.75rem',

    [`&.${menuClasses.disabled}`]: {
      color: 'grey',
    },
    '&:hover': {
      borderRadius: '0.25rem',
      background: 'var(--zocks-gray-800)',
      color: 'white',
    },
    [`&.active`]: {
      borderRadius: '0.25rem',
      color: 'white',
      backgroundColor: 'var(--zocks-primary)',
      i: { color: 'white' },
    },
  },
  label: ({ open }) => ({
    fontWeight: open ? 600 : undefined,
  }),
}

const ZocksMenuContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
`

export const LayoutWithLeftSidebar: React.FC<LayoutProps> = ({ containerClassName, children }) => {
  useIframeMessageHandler()
  const dispatch = useAppDispatch()
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const [isSidebarFixed, setSidebarFixed] = useState<boolean>(true)

  const onSidebarMouseEnter = () => {
    if (!isSidebarFixed) {
      setCollapsed(false)
    }
  }
  const onSidebarMouseLeave = () => {
    if (!isSidebarFixed) {
      setCollapsed(true)
    }
  }

  const toggleSidebarFixed = () => {
    const newFixed = !isSidebarFixed
    setSidebarFixed(newFixed)
    setCollapsed(!newFixed)
  }

  const toasts = useAppSelector(selectToasts)

  return (
    <Container fluid className='d-flex flex-column p-0 h-100'>
      <ToastContainer position='bottom-center' className='p-3 position-fixed'>
        {toasts.map((toast) => (
          <Toast key={toast.id} onClose={() => dispatch(removeToast(toast.id))} variant={toast.variant} title={toast.title} delay={toast.delay}>
            {toast.messages && (
              <ul>
                {toast.messages.map((message, i) => (
                  <li key={`${toast.id}-message-${i}`}>{message}</li>
                ))}
              </ul>
            )}
          </Toast>
        ))}
      </ToastContainer>
      <Row className='content-row'>
        <Col className='p-0 h-100'>
          <Container fluid className='p-0 h-100'>
            <div className='h-100 d-flex'>
              <div className={classNames('d-lg-block d-none zocks-sidebar-container')}>
                <Sidebar
                  className='zocks-sidebar h-100'
                  collapsed={collapsed}
                  toggled={isSidebarFixed}
                  onBackdropClick={() => setSidebarFixed(false)}
                  customBreakPoint='992'
                  onMouseEnter={onSidebarMouseEnter}
                  onMouseLeave={onSidebarMouseLeave}
                  collapsedWidth='72px'
                  width='232px'
                  rootStyles={{
                    [`.${sidebarClasses.container}`]: {
                      backgroundColor: 'var(--zocks-gray-900)',
                      borderRadius: '0 1rem 1rem 0',
                      overflow: 'visible',
                    },
                    borderColor: 'var(--zocks-gray-900)',
                    backgroundColor: 'var(--zocks-gray-900)',
                    borderRadius: '0 1rem 1rem 0',
                  }}
                >
                  <div className='d-flex flex-column h-100'>
                    <SidebarHeader isSidebarFixed={isSidebarFixed} toggleSidebar={toggleSidebarFixed} collapsed={collapsed} />
                    <ZocksMenuContainer>
                      <ZocksMenu collapsed={collapsed} />
                    </ZocksMenuContainer>
                    <SidebarFooter collapsed={collapsed} menuItemStyles={menuItemStyles} />
                  </div>
                </Sidebar>
              </div>
              <FloatingMenu />
              <div
                className={classNames('d-flex flex-column w-100', {
                  'content--not-fixed': !isSidebarFixed,
                  'content--fixed': isSidebarFixed,
                })}
              >
                <HeaderContextProvider>
                  <LayoutHeader />
                  <Container className={classNames('mt-6 content-size zocks-pb-10.0 px-0', containerClassName)}>{children ?? <Outlet />}</Container>
                </HeaderContextProvider>
              </div>
            </div>
          </Container>
        </Col>
      </Row>
    </Container>
  )
}

const FloatingMenu = () => {
  const menuRef = useRef(null)
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => {
    setMenuOpen(false)
  })

  useEffect(() => {
    if (!menuRef.current) {
      return
    }
    if (isMenuOpen) {
      disableBodyScroll(menuRef.current)
    } else {
      enableBodyScroll(menuRef.current)
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [isMenuOpen])

  const onMenuItemClick = () => {
    setMenuOpen(false)
  }

  return (
    <Container ref={containerRef} className='d-flex flex-column d-lg-none floating-menu-wrapper '>
      <Row>
        <Col xs={12} sm={8} md={6}>
          <Sidebar
            ref={menuRef}
            className={classNames('floating-sidebar zocks-sidebar', { 'd-none': !isMenuOpen })}
            width='100%'
            rootStyles={{
              [`.${sidebarClasses.container}`]: {
                backgroundColor: 'var(--zocks-gray-900)',
                borderRadius: '0 1rem 1rem 0',
              },
              borderColor: 'var(--zocks-gray-900)',
              backgroundColor: 'var(--zocks-gray-900)',
              borderRadius: '0 1rem 1rem 0',
            }}
          >
            <div className='d-flex flex-column'>
              <ZocksMenuContainer className='pt-4 pb-3 mb-0'>
                <ZocksMenu collapsed={false} onItemClick={onMenuItemClick} />
              </ZocksMenuContainer>
              <SidebarFooter collapsed={false} menuItemStyles={menuItemStyles} onItemClick={onMenuItemClick} />
            </div>
          </Sidebar>
          <div
            role='button'
            onClick={() => {
              setMenuOpen(!isMenuOpen)
            }}
            className='hamburger-fab'
          >
            {isMenuOpen ? <i className='bi bi-x' /> : <i className='bi bi-list' />}
          </div>
        </Col>
      </Row>
    </Container>
  )
}

interface ZocksMenuProps {
  collapsed: boolean
  onItemClick?: () => void
}

const ZocksMenu: React.FC<ZocksMenuProps> = ({ collapsed, onItemClick }) => {
  const { hasQuota } = useZocksQuota()
  const insightsEnabled = isInsightsEnabled()
  const emailAvailable = hasQuota('integration.emailEnabled.value')
  const handleMenuItemClick = useTrackedMenuItem(onItemClick)

  return (
    <>
      <div className='d-flex flex-column zocks-gap-2.5 zocks-p-1.25'>
        <div className='d-flex flex-column gap-2'>
          <div className={classNames('lead-small pe-4 zocks-text-gray-500 text-nowrap', { 'opacity-0': collapsed })}>Meetings</div>
          <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
            <MenuItem onClick={() => handleMenuItemClick('liveMeetings')} icon={<LiveIcon />} component={<NavLink to='/workspace/live-meetings' />}>
              <div className='button-small'>Live Meetings</div>
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuItemClick('upcoming')}
              icon={<i className='bi bi-calendar4-week' />}
              component={<NavLink to='/workspace/upcoming' />}
            >
              <div className='button-small'>Upcoming</div>
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuItemClick('pastMeetings')}
              icon={<i className='bi bi-collection' />}
              component={<NavLink to='/workspace/past-meetings' />}
            >
              <div className='button-small'>Past Meetings</div>
            </MenuItem>

            <ScopedBlock
              scope={{
                scope: 'room_session:start',
                superScope: ['room_session:admin', 'room:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <MenuItem
                onClick={() => handleMenuItemClick('inPersonMeeting')}
                icon={<i className='bi bi-record-circle' />}
                component={<NavLink to='/workspace/in-person-meeting/mobile-support' />}
              >
                <div className='button-small'>In Person Meeting</div>
              </MenuItem>
            </ScopedBlock>
          </Menu>
        </div>
      </div>
      <Divider className='zocks-bg-color-gray-800 divider-sidebar-menu' />
      <div className='d-flex flex-column zocks-gap-2.5 zocks-p-1.25'>
        <div className='d-flex flex-column gap-2'>
          <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
            <FeatureAdvertisement bypass={emailAvailable}>
              <ScopedBlock
                scope={{
                  scope: emailAvailable ? 'email_type:list' : 'room:join', // room:join link should be clickable for all users when the quota is disabled because we want to suggest them to upgrade their subscription.
                  superScope: ['email_type:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <MenuItem
                  onClick={() => handleMenuItemClick('inbox')}
                  icon={
                    <div className='d-inline-flex position-relative'>
                      <i className='bi bi-envelope' />
                    </div>
                  }
                  component={<NavLink to='/email/inbox' />}
                >
                  <div className='button-small'>Inbox</div>
                </MenuItem>
              </ScopedBlock>
            </FeatureAdvertisement>
            <MenuItem
              onClick={() => handleMenuItemClick('contacts')}
              icon={<i className='bi bi-person-lines-fill' />}
              component={<NavLink to='/library/manage-contacts' />}
            >
              <div className='button-small'>Contacts</div>
            </MenuItem>
            <ScopedBlock
              scope={{
                scope: 'dashboard:admin',
                superScope: [],
              }}
              missingScopeAction={MissingScopeAction.HIDE}
            >
              <FeatureAdvertisement bypass={hasQuota('insights.enabled.value')}>
                {insightsEnabled ? (
                  <MenuItem
                    onClick={() => handleMenuItemClick('insights')}
                    icon={<i className='bi bi-lightbulb' />}
                    component={<NavLink to='/analytics-hub/insights' />}
                  >
                    <div className='d-flex align-items-center justify-content-between w-100'>
                      <div className='button-small'>Insights</div>
                      <div className='button-small zocks-py-0.125 px-1 zocks-border-0.25 bg-white zocks-text-primary'>New</div>
                    </div>
                  </MenuItem>
                ) : null}
                <MenuItem
                  onClick={() => handleMenuItemClick('dashboard')}
                  icon={<i className='bi bi-graph-up' />}
                  component={<NavLink to='/analytics-hub' end />}
                >
                  <div className='button-small'>Analytics</div>
                </MenuItem>
              </FeatureAdvertisement>
            </ScopedBlock>
            <MenuItem onClick={() => handleMenuItemClick('cross-client')} icon={<i className='bi bi-x-diamond' />} component={<NavLink to='/cross-client' />}>
              <div className='d-flex align-items-center justify-content-between w-100'>
                <div className='button-small'>Client Queries</div>
                <div className='button-small zocks-py-0.125 px-1 zocks-border-0.25 bg-white zocks-text-primary'>New</div>
              </div>
            </MenuItem>
          </Menu>
        </div>
      </div>
      <Divider className='zocks-bg-color-gray-800 divider-sidebar-menu' />
      <div className='d-flex flex-column zocks-gap-2.5 zocks-p-1.25'>
        <div className='d-flex flex-column gap-2'>
          <div className={classNames('lead-small pe-4 zocks-text-gray-500', { 'opacity-0': collapsed })}>Settings</div>
          <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
            <MenuItem onClick={() => handleMenuItemClick('setup')} icon={<i className='bi bi-sliders' />} component={<NavLink to='/workspace/setup' />}>
              <div className='button-small'>Setup</div>
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuItemClick('profileSettings')}
              icon={<i className='bi bi-person fw-600' />}
              component={<NavLink to='/account/profile-settings' />}
            >
              <div className='button-small'>Profile</div>
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuItemClick('integrationSettings')}
              icon={<i className='bi bi-grid fw-600' />}
              component={<NavLink to='/account/integration-settings' />}
            >
              <div className='button-small'>Integrations</div>
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuItemClick('manageTeam')}
              icon={<i className='bi bi-diagram-3' />}
              component={<NavLink to='/account/manage-team' />}
            >
              <div className='button-small'>Team</div>
            </MenuItem>
            <ScopedBlock
              scope={{
                scope: 'billing_subscription:change',
                superScope: ['billing_subscription:admin'],
              }}
              missingScopeAction={MissingScopeAction.HIDE}
            >
              <MenuItem
                onClick={() => handleMenuItemClick('plan')}
                icon={<i className='bi bi-credit-card-2-back' />}
                component={<NavLink to='/account/plan' />}
              >
                <div className='button-small'>Plan</div>
              </MenuItem>
            </ScopedBlock>
          </Menu>
        </div>
      </div>
    </>
  )
}
